/**********************************************************************
// @@@ START COPYRIGHT @@@
// AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
// Copyright 2018 - 2019
// Avi Solar Energy Pvt Ltd.
// Protected as an unpublished work.
// The computer program listings, specifications and documentation
// herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
// and shall not be reproduced, copied, disclosed, or used in whole or in part for
// any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
**********************************************************************/
<template>
  <div>
    <div class="overlay"></div>
    <FloatingButtonMenu
      :menu="floatComponents"
      @select="floatMenuSelectHandler"
    />
    <v-dialog v-model="showMap" persistent width="500">
      <MapComponent
        :key="componentKey"
        v-if="showMap"
        @close="closeDialogHandler"
      />
    </v-dialog>
    <v-dialog v-model="showStaticInfo" persistent width="700">
      <InformationComp @close="closeDialogHandler"/>
    </v-dialog>
    <v-layout row wrap>
      <v-flex sm12 md9 pt-0>
        <Tabs v-model="selectedTab" show-arrows id="select-block">
          <v-tab
            v-for="(item, index) in computedBlockList"
            :key="item.id"
            class="activeTab"
            :href="'#tab-' + index"
            @click="tabChange(item)"
          >
            <span class="primaryText--text">
              {{ item.text }}
            </span>
          </v-tab>
          <v-tabs-items>
            <v-tab-item
              v-for="(item, index) in computedBlockList"
              :key="item.id"
              centered
              :value="'tab-' + index"
            >
            </v-tab-item>
          </v-tabs-items>
        </Tabs>
      </v-flex>
      <v-flex sm12 md3 text-sm-right d-flex>
        <div class="note">
          *YTC: {{ $t("Yet To Compute") }} , *NA: {{ $t("Not Available") }}
        </div>
      </v-flex>
      <v-flex xs12 md5>
        <Card class="table-card">
          <v-data-table
            :headers="headers"
            :items="tableData"
            item-key="name"
            hide-actions
            class="elevation-1 custom-data-table"
          >
            <template slot="header" slot-scope="{ header }">
              <tr>
                <th
                  v-for="col in headers"
                  :key="col.text"
                  :style="{ width: col.width }"
                >
                  {{ header[col.value] }}
                </th>
              </tr>
            </template>
            <template v-slot:items="props">
              <td class="text-xs-left parameter-name">
                {{ $t(props.item.name) }}
                <span class="unit"> ({{ props.item.unit }})</span>
              </td>
              <td class="text-xs-center"> 
                <span
                  :class="
                    props.item.today != null &&
                    props.item.today != 'No Data' &&
                    props.item.today != '-'
                      ? 'number--text value'
                      : 'error--text no-value'
                  "
                  >{{ props.item.today == null ? '-' : props.item.today }}</span
                >
              </td>
              <td class="text-xs-center">
                <span
                  :class="
                    props.item.yesterday != null &&
                    props.item.yesterday != 'No Data' &&
                    props.item.yesterday != '-'
                      ? 'number--text value'
                      : 'error--text no-value'
                  "
                  >{{ props.item.yesterday == null ? '-' : props.item.yesterday }}</span
                >
              </td>
              <td class="text-xs-center">
                <span
                  :class="
                    props.item.month != null &&
                    props.item.month != 'No Data' &&
                    props.item.month != '-'
                      ? 'number--text value'
                      : 'error--text no-value'
                  "
                  >{{ props.item.month == null ? '-' : props.item.month }}</span
                >
              </td>
              <td class="text-xs-center">
                <span
                  :class="
                    props.item.year != null &&
                    props.item.year != 'No Data' &&
                    props.item.year != '-'
                      ? 'number--text value'
                      : 'error--text no-value'
                  "
                  >{{ props.item.year == null ? '-' : props.item.year }}</span
                >
              </td>
            </template>
          </v-data-table>
        </Card>
      </v-flex>
      <v-flex xs12 md7>
        <AvailabilityComp
          :isDark="isDark"
          :items="widgetDataSet"
          :generation="todayGen"
          :lifeTimeGeneration="lifeTimeGeneration"
          :cardDataSet="cardDataSet"
        />
      </v-flex>
      <v-flex xs12 md5>
        <Card class="map-card">
          <div class="text-xs-center pt-3 pb-3">{{ $t(left.title) }}</div>
          <v-divider class="custom-divider" />
          <div class="pa-3">
            <GlobalGraph
              class="graph"
              v-if="computedSeries['left'].length"
              graphID="1"
              :series="computedSeries['left']"
              popUpWidth="15vw"
              :xAxisData="xAxisData['left']"
              :yAxis="computedYAxis['left']"
              :theme="theme"
            />
          </div>
        </Card>
      </v-flex>
      <v-flex xs12 md7>
        <Card class="map-card">
          <div class="text-xs-center pt-3 pb-3">{{ $t(right.title) }}</div>
          <v-divider class="custom-divider" />
          <div class="pa-3">
            <GlobalGraph
              class="graph"
              v-if="computedSeries['right'].length"
              graphID="2"
              :series="computedSeries['right']"
              popUpWidth="15vw"
              :xAxisData="xAxisData['right']"
              :yAxis="computedYAxis['right']"
              :theme="theme"
            />
          </div>
        </Card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import CommonApi from "@/services/Common";
import PlantOverviewAPI from "@/services/PlantOverview";
import { mapGetters, mapMutations } from "vuex";
import { mapGettersMixin } from "@/util/mixin/index";
const FloatingButtonMenu = () =>
  import("@/components/common/FloatingButtonMenu");
const MapComponent = () =>
  import("@/components/Plant Overview New/MapComponent");

const AvailabilityComp = () =>
  import("@/components/utility/AvailabilityComponent");
const InformationComp = () =>
  import("@/components/utility/InformationComponent");
const moment = require("moment");
const momentTz = require('moment-timezone');

export default {
  mixins: [mapGettersMixin],
  components: {
    FloatingButtonMenu,
    MapComponent,
    AvailabilityComp,
    InformationComp,
  },
  data() {
    return {
      componentKey: 0,
      widgetDataSet: [],
      showMap: false,
      showStaticInfo: false,
      selectedTab: "plant",
      tableData: [],
      todayGen: 0,
      cardDataSet: [],
      lifeTimeGeneration: 0,
      blockId: 1,
      blockList: [],
      series: {
        left: [],
        right: []
      },
      xAxisData: {
        left: [],
        right: []
      },
      yAxis: {
        left: [
          {
            type: "value",
            name: "",
            min: 0,
            position: "left",
            axisLabel: {
              formatter: "{value}",
              rotate: 40,
            },
          },
        ],
        right: [
          {
            type: "value",
            name: "",
            min: 0,
            position: "left",
            axisLabel: {
              formatter: "{value}",
              rotate: 40,
            },
          },
        ]
      },
      left: {
        isMap: false,
        title: ''
      },
      right: {
        isMap: false,
        title: ''
      }
    };
  },
  computed: {
    ...mapGetters(["getSelectedPlantId", "getActivePlantInfo", "getRules", "getFloatingButton"]),
    floatComponents() {
      const rule = _.cloneDeep(this.getRules(["rule", "access_config"]))
      if (rule) {
        const componentList = Object.values(rule).reduce((list, el) => {
          if (Array.isArray(el)) {
            return [...list, ...el];
          } else {
            return [...list, el];
          }
        }, []);
        const componentObj = componentList.find(
          (el) => el.path === `/${this.$route.path.split("/").slice(-1)[0]}`
        );
        return componentObj
          ? [...this.getFloatingButton(componentObj.component), {
          "icon": "feed",
          "name": "STATIC\nINFO",
          "component": "static"
        }] || []
          : [
            {
              "icon": "feed",
              "name": "STATIC\nINFO",
              "component": "static"
            }
          ];
      }
      return [
        {
          "icon": "feed",
          "name": "STATIC\nINFO",
          "component": "static"
        }
      ];
    },
    computedBlockList() {
      if (this.blockList.length > 0) {
        const cloneBlockList = _.cloneDeep(this.blockList);
        cloneBlockList[0].text = this.$t(cloneBlockList[0].text);
        return cloneBlockList;
      } else {
        return [];
      }
    },
    showFloat() {
      return (
        this.floatComponents !== undefined && !!this.floatComponents.length
      );
    },
    headers() {
      return [
        {
          text: this.$t("KPI Metrics"),
          align: "left",
          value: "name",
          width: "150px",
          sortable: false,
        },
        {
          text: this.$t("Today"),
          value: "today",
          align: "center",
          width: "90px",
          sortable: false,
        },
        {
          text: this.$t("Yesterday"),
          value: "yesterday",
          align: "center",
          width: "90px",
          sortable: false,
        },
        {
          text: this.$t("This month"),
          value: "month",
          align: "center",
          width: "90px",
          sortable: false,
        },
        {
          text: this.$t("This year"),
          value: "year",
          align: "center",
          width: "90px",
          sortable: false,
        },
      ]
    },
    computedSeries() {
      const series = _.cloneDeep(this.series)
      Object.keys(series).forEach(position => {
        series[position].forEach((seriesObj) => {
          seriesObj.name = seriesObj.name.replace(seriesObj.parameterName, this.$t(seriesObj.parameterName));
          seriesObj.name = seriesObj.name.replace(seriesObj.device, this.$t(seriesObj.device));
        })
      })

      return series
    },
    computedYAxis() {
      const yAxis = _.cloneDeep(this.yAxis)
      Object.keys(yAxis).forEach(position => {
        yAxis[position].forEach(yAxisObj => {
          yAxisObj.name = yAxisObj.name ? this.$t(yAxisObj.name) : ''
        })
      })

      return yAxis
    }
  },
  async mounted() {
    await this.getBlockList();
    this.setIntervalWithoutDelay(this.callAllApi);
  },
  methods: {
    ...mapMutations(["SET_LAST_CALL_TIME", "SET_PROGRESS_BAR"]),
    initializeData() {
      this.widgetDataSet = []
      this.showMap = false
      this.showStaticInfo = false
      this.tableData = []
      this.todayGen = 0
      this.cardDataSet = []
      this.lifeTimeGeneration = 0
    },
    closeDialogHandler() {
      document.querySelector('.overlay').style.display = 'none'
      this.showMap = false
      this.showStaticInfo = false
    },
    callAllApi() {
      this.SET_PROGRESS_BAR(true)
      let updateTime = moment().format("MM-DD-YYYY H:mm:ss");
      this.SET_LAST_CALL_TIME(updateTime);
      let responseList = []
      responseList.push(this.getWidgetData());
      responseList.push(this.getGraphData());
      if (this.selectedTab === "plant") {
        responseList.push(this.getPlantLevelData());
      } else {
        this.initializeData()
        responseList.push(this.getBlockLevelData());
      }

      Promise.all(responseList).then(res => {
        this.SET_PROGRESS_BAR(false)
      }).catch(err => {
        console.log(err, 'ERROR')
        this.SET_PROGRESS_BAR(false)
      })
    },
    floatMenuSelectHandler(selection) {
      if (selection.component === "map") {
        this.showMap = true;
        document.querySelector('.overlay').style.display = 'block'
      } else if (selection.component === "static") {
        this.showStaticInfo = true
        document.querySelector('.overlay').style.display = 'block'
      } else if (selection.component === "document") {
        window.open(selection.path);
      } else {
        this.$router.push(selection.path);
      }
    },
    tabChange(item) {
      if (item.id === "plant") {
        this.selectedTab = "plant";
      } else {
        this.selectedTab = "block";
        this.blockId = item.id;
      }
      this.setIntervalWithoutDelay(this.callAllApi);
    },
    async getBlockList() {
      let response = await CommonApi.getBlockList(this.getSelectedPlantId);
      this.blockList = response.data.message;

      if (this.blockList.length === 1) {
        this.blockList = [
          {
            id: "plant",
            text: "Plant Details",
          },
        ];
      } else {
        this.blockList.unshift({
          id: "plant",
          text: "Plant Details",
        });
      }
    },
    async getPlantLevelData() {
      await this.getCardData();
      await this.getKpiData();
    },
    async getBlockLevelData() {
      await this.getBlockCardData();
      await this.getBlockKpiData();
    },
    async getGraphData() {
      const response = await PlantOverviewAPI.getGraphPlant(
        this.getSelectedPlantId,
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X"),
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X")
      );
      
      const positionList = ['left', 'right']
      for (let position of positionList) {
        const tempData = {};
        let xAxisData = [];
        let data = {}

        Object.keys(response.data.message[position].data).forEach((deviceName, index) => {
          if (deviceName == 'weather station' && Object.keys(response.data.message[position].data).length > 1) {
            this.yAxis[position][0].name = 'Other Parameters'
            this.yAxis[position].push({
              type: "value",
              name: "WS Parameters",
              min: 0,
              position: "right",
              axisLabel: {
                formatter: "{value}",
                rotate: 40,
              },
            })
          } else {
            this.yAxis[position][0].name = 'WS Parameters'
          }
          Object.keys(response.data.message[position].data[deviceName]).forEach((parameter, i) => {
            response.data.message[position].data[deviceName][parameter].forEach(el => {
              if (!data[parameter]) {
                data[parameter] = {
                  device: deviceName,
                  data: []
                }
              }
              data[parameter].data.push(el[parameter])
              if (index == 0 && i == 0) {
                xAxisData.push(el['Sensor Time'])
              }
            })
          })
        })

        response.data.message[position].headers.forEach((el) => {
          if (el.text != "Sensor Time") {
            tempData[el.text] = {
              name: `${el.text} ${el.unit ? `(${el.unit})` : ""}`,
              type: "line",
              device: el.deviceName,
              parameterName: el.name,
              large: true,
              data: data[el.text].data,
            };
            if (data[el.text].device == 'weather station') {
              tempData[el.text]['yAxisIndex'] = 1
            }
          }
        });
        const graphName = response.data.message[position].graphName
        this[position] = {
          isMap: false,
          title: graphName.toUpperCase()
        }
        this.xAxisData[position] = xAxisData;
        this.series[position] = Object.values(tempData);
      }
    },
    async getWidgetData() {
      const response = await PlantOverviewAPI.getWidgetPlant(
        this.getSelectedPlantId,
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X"),
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X")
      );
      this.widgetDataSet = response.data.message;
    },
    async getKpiData() {
      const response = await PlantOverviewAPI.getKpiPlant(
        this.getSelectedPlantId,
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X")
      );
      const tempArr = [];
      response.data.message.parameter.forEach((param) => {
        const obj = {
          name: param.name,
          unit: param.unit,
        };
        Object.keys(response.data.message.data).forEach((el) => {
          obj[el] = response.data.message.data[el][param.parameter];
        });
        tempArr.push(obj);
      });
      this.tableData = tempArr;
      // If today's energy is available or not in table
      // If it is available check the unit of energy / if not available its always kWh
      const energyParamObj = response.data.message.parameter.filter(el => el.parameter == 'energy_inv')
      let factor = 1
      if (energyParamObj.length > 0 && energyParamObj[0].unit == 'MWh') {
        factor = 1000
      }
      this.todayGen = parseFloat(response.data.message.data.today.energy_inv) * factor
    },
    async getBlockKpiData() {
      const response = await PlantOverviewAPI.getKpiBlock(
        this.getSelectedPlantId,
        this.blockId,
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X")
      );
      const tempArr = [];
      response.data.message.parameter.forEach((param) => {
        const obj = {
          name: param.name,
          unit: param.unit,
        };
        Object.keys(response.data.message.data).forEach((el) => {
          obj[el] = response.data.message.data[el][param.parameter];
        });
        tempArr.push(obj);
      });
      // If today's energy is available or not in table
      // If it is available check the unit of energy / if not available its always kWh
      const energyParamObj = response.data.message.parameter.filter(el => el.parameter == 'energy_inv')
      let factor = 1
      if (energyParamObj.length > 0 && energyParamObj[0].unit == 'MWh') {
        factor = 1000
      }
      this.tableData = tempArr;
      this.todayGen = parseFloat(response.data.message.data.today.energy_inv) * factor;
    },
    async getCardData() {
      const response = await PlantOverviewAPI.getCardPlant(
        this.getSelectedPlantId,
        momentTz("05:00:00", "HH:mm:ss").tz(this.getActivePlantInfo('timezone')).format("X"),
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X")
      );
      const lifeTimeObj = response.data.message.find(
        (el) => el.name === "Lifetime Generation (INV)"
      );

      this.lifeTimeGeneration = lifeTimeObj.value * this.calcUnit(lifeTimeObj.unit)
      this.cardDataSet = response.data.message.filter((el) => el.showCard).map(el => {
        if (el.value == null) {
          el.value = '-'
        }
        return el
      });
    },
    getBlockCardData() {
      PlantOverviewAPI.getCardBlock(
        this.getSelectedPlantId,
        this.blockId,
        momentTz("05:00:00", "HH:mm:ss").tz(this.getActivePlantInfo('timezone')).format("X"),
        momentTz().tz(this.getActivePlantInfo('timezone')).format("X")
      ).then((response) => {
        const lifeTimeObj = response.data.message.find(
          (el) => el.name === "Lifetime Generation (INV)"
        );

        this.lifeTimeGeneration = lifeTimeObj.value * this.calcUnit(lifeTimeObj.unit)
        this.cardDataSet = response.data.message.filter((el) => el.showCard).map(el => { 
          if (el.value == null) {
            el.value = '-'
          }
          return el
        });
      });
    },
    calcUnit(unit) {
      switch (unit) {
        case 'kWh':
          return 1
        case 'MWh':
          return 1000
        case 'GWh':
          return 1000000
      }
    }
  },
  watch: {
    async getSelectedPlantId() {
      this.initializeData()
      this.selectedTab = "plant";
      this.blockId = 1
      this.blockList = []
      this.series = {
        left: [],
        right: []
      }
      this.xAxisData = {
        left: [],
        right: []
      }
      this.left = {
        isMap: false,
        title: ''
      }
      this.right = {
        isMap: false,
        title: ''
      }
      await this.getBlockList();
      this.setIntervalWithoutDelay(this.callAllApi);
    },
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi);
      }
    },
    "$i18n.locale": {
      handler() {
        this.componentKey++;
      },
    },
  },
};
</script>

<style>
  .parameter-name {
    font-size: 14px;
    padding: 0px 10px !important;
  }

  .table-card {
    height: 580px;
    border-radius: 8px;
    box-shadow: 0px 5px 5px 0px #334079;
  }

  .custom-data-table {
    max-height: 580px;
    overflow-y: auto;
    border-radius: 8px !important;
    border: 1px solid #334079;
  }

  /* To add vertical lines between columns */
  .custom-data-table thead th:not(:last-child),
  .custom-data-table tbody td:not(:last-child) {
    border-right: 1px solid #334079;
  }

  .custom-data-table thead th {
    height: 48px;
    font-size: 14px;
    font-weight: 700 !important;
  }

  .custom-data-table th {
    z-index: 1;
    position: sticky;
    top: 0;
  }

  .theme--dark .custom-data-table thead th {
    color: #fff !important;
  }

  .theme--light .custom-data-table thead th {
    color: #000 !important;
  }

  .custom-data-table thead th:not(:first-child) {
    padding: 0px !important;
  }
</style>

<style scoped>
  .overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: none;
    z-index: 2;
    cursor: pointer;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }

  .graph {
    width: 100%;
    height: 370px;
  }

  .activeTab:hover {
    background-color: var(--v-sixth-base);
  }

  .note {
    margin: auto;
    color: #fbbc05;
  }

  .unit {
    color: #aeaeae;
  }

  .value {
    font-size: 14px;
    font-weight: 600;
  }

  .no-value {
    font-size: 14px;
    font-weight: 600;
  }

  .map-card {
    height: 486px;
    border-radius: 8px;
    box-shadow: 0px 5px 5px 0px #334079;
  }

  .custom-divider {
    background-color: #334079;
  }
</style>
